/**
 * BotonContrato.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Botón para generar un contrato PDF de prestación de servicios desde el evento.
 * Reutiliza la plantilla ContratoPDF y descarga el archivo al navegador.
 */
import React, { useState } from "react";
import { pdf } from "@react-pdf/renderer";
import {
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { PictureAsPdf as PdfIcon } from "@mui/icons-material";
import ContratoPDF from "./ContratoPDF";
import logoSrc from "../images/R1.webp";

const toDataUrlPng = (src) =>
  new Promise((resolve) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const folioStr = (id) => String(id || "0").padStart(4, "0");

const BotonContrato = ({ evento, variant = "button", size = "small", sx = {} }) => {
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const handleGenerar = async () => {
    if (!evento) return;
    setLoading(true);

    try {
      const logoUrl = await toDataUrlPng(logoSrc);
      const blob = await pdf(<ContratoPDF evento={evento} logoUrl={logoUrl} />).toBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Contrato-${folioStr(evento.id)}-${(evento.cliente_nombre || "Cliente").replace(/\s+/g, "_").replace(/[^a-zA-Z0-9_-]/g, "")}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      setSnack({ open: true, msg: "Contrato generado y descargado correctamente", sev: "success" });
    } catch (err) {
      console.error("[BotonContrato] Error al generar PDF:", err);
      setSnack({ open: true, msg: "Error al generar el contrato", sev: "error" });
    } finally {
      setLoading(false);
    }
  };

  const icon = loading ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : <PdfIcon fontSize={size === "large" ? "medium" : "small"} />;

  return (
    <>
      {variant === "icon" ? (
        <Tooltip title={loading ? "Generando contrato..." : "Generar contrato en PDF"}>
          <span>
            <IconButton
              size={size}
              onClick={handleGenerar}
              disabled={loading || !evento}
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: loading ? "rgba(0,0,0,0.04)" : "rgba(34, 197, 94, 0.16)",
                color: loading ? "action.disabled" : "#22C55E",
                ...sx,
              }}
            >
              {icon}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Tooltip title={loading ? "Generando contrato..." : "Generar contrato en PDF"}>
          <span>
            <Button
              variant="outlined"
              size={size}
              startIcon={icon}
              onClick={handleGenerar}
              disabled={loading || !evento}
              sx={{
                borderColor: loading ? "rgba(0,0,0,0.12)" : "#22C55E",
                color: loading ? "text.disabled" : "#22C55E",
                fontWeight: 600,
                letterSpacing: 0.4,
                ...sx,
              }}
            >
              {loading ? "Generando..." : "Contrato PDF"}
            </Button>
          </span>
        </Tooltip>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BotonContrato;
